import React from 'react';
import Dropzone from 'react-dropzone';
import {css, view} from '@sail/ui';
import {MIME_TYPES_ACCEPTED_BY_API} from '@dashboard/data/types/mime_types';
import {useIntl} from '@sail/intl';
import type {MessageDescriptor} from '@sail/intl';
import type {UploadedFile} from './FileUploaderFileListItem';
import {messages} from './messages';
import {generateUniqueFileID, getTotalFileSize} from './utils';
import {hasKey} from '../../utils/typing';
import {Button} from '../../SharedComponents/Button/Button';

const getFileExtensions = (acceptableFileTypes: readonly string[]): string => {
  const acceptArr = acceptableFileTypes
    .filter((mimeType) => hasKey(MIME_TYPES_ACCEPTED_BY_API, mimeType))
    .map((mimeType) => {
      return (
        hasKey(MIME_TYPES_ACCEPTED_BY_API, mimeType) &&
        MIME_TYPES_ACCEPTED_BY_API[mimeType]
      );
    });
  acceptArr.sort();
  return acceptArr.join(', ');
};

type Props = {
  id?: string;
  acceptableFileTypes?: readonly string[];
  disabled?: boolean;
  handleShowFileLimitError?: (b: boolean) => void;
  inputSubtitle?: MessageDescriptor;
  inputTitle?: MessageDescriptor;
  maxSize?: number;
  onChange: (files: UploadedFile[]) => void;
  onSelected?: (files: File[]) => void;
  uploadedFiles: UploadedFile[];
  suffix?: string;
  suffixLabel?: React.ReactNode;
  name?: string;
  singleFile?: boolean;
  enableDropZone?: boolean;
};

export const FileUploaderInput: React.FC<Props> = ({
  acceptableFileTypes,
  disabled,
  handleShowFileLimitError,
  inputSubtitle,
  inputTitle,
  maxSize,
  onChange,
  onSelected,
  uploadedFiles,
  suffix,
  suffixLabel,
  name,
  singleFile,
  id,
  enableDropZone,
}) => {
  const intl = useIntl();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [key, setKey] = React.useState(Date.now());
  const maxFilesReached = singleFile && uploadedFiles.length > 0;

  const processFiles = (newFiles: File[]) => {
    const totalSize =
      getTotalFileSize(uploadedFiles) +
      newFiles.reduce((acc, f) => acc + f.size, 0);

    if (maxFilesReached) return;

    if (!maxSize || totalSize <= maxSize) {
      const allFiles: UploadedFile[] = [
        ...uploadedFiles,
        ...newFiles.map((file, idx) => ({
          id: generateUniqueFileID(idx),
          created: new Date().getTime() / 1000,
          localFile: file,
        })),
      ];
      if (onSelected) {
        onSelected(newFiles);
      }
      onChange(allFiles);
      if (handleShowFileLimitError) {
        handleShowFileLimitError(false);
      }
    } else if (handleShowFileLimitError) {
      handleShowFileLimitError(true);
    }
  };

  /**
   * When uploading new files, we have to check:
   *    - Total file size < maximumFileSize
   *    - Upload files and show loading state
   *    - Link file to an evidence category
   */
  const handleUpload = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      const target: HTMLInputElement = event.currentTarget;
      const newFiles = Array.from(target.files ?? []);

      processFiles(newFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadedFiles],
  );

  const handleFileDrop = (acceptedFiles: File[]) => {
    processFiles(acceptedFiles);
  };

  const renderSubtitle = () => {
    if (inputSubtitle) {
      return intl.formatMessage(inputSubtitle);
    }

    if (acceptableFileTypes && acceptableFileTypes.length > 0) {
      return intl.formatMessage(messages.supportedFiles, {
        acceptableFileTypes: getFileExtensions(acceptableFileTypes),
        maxSize:
          maxSize &&
          intl.formatNumber(maxSize / 1e6, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
          }),
      });
    }

    return intl.formatMessage(messages.uploadFilesFallbackMessage);
  };

  if (maxFilesReached) {
    return null;
  }

  const uploadInput = (
    <view.div>
      <view.input
        ref={inputRef}
        id={id}
        key={key}
        type="file"
        accept={acceptableFileTypes ? acceptableFileTypes.join(',') : ''}
        multiple={!singleFile}
        uses={[css({display: 'none'})]}
        onChange={(event: React.SyntheticEvent<HTMLInputElement, Event>) => {
          handleUpload(event);
          setKey(Date.now()); // reset value so we can upload the same file again - https://stackoverflow.com/questions/42192346/how-to-reset-reactjs-file-input/55495449#55495449
        }}
        disabled={disabled}
        name={name}
      />
      <Button
        onPress={() => inputRef.current?.click()}
        css={{
          stack: 'y',
          alignY: 'center',
          alignX: 'center',
          padding: 'space.300',
          whiteSpace: 'break-spaces',
          textAlign: 'center',
          borderColor: 'border',
          borderStyle: 'dashed',
        }}
        disabled={disabled}
        maximized
      >
        {inputTitle || inputSubtitle ? (
          <view.div
            css={{
              stack: 'y',
              alignY: 'center',
              alignX: 'center',
              whiteSpace: 'break-spaces',
              textAlign: 'center',
            }}
          >
            <view.span
              css={{
                font: 'body.small.emphasized',
                color: 'action.primary',
              }}
            >
              {inputTitle
                ? intl.formatMessage(inputTitle)
                : intl.formatMessage(messages.inputHeader)}
            </view.span>
            <view.span
              css={{
                font: 'label.small.emphasized',
                color: 'subdued',
              }}
            >
              {renderSubtitle()}
            </view.span>
          </view.div>
        ) : (
          <view.div css={{stack: 'x'}}>
            <Button type="primary" onPress={() => inputRef.current?.click()}>
              {intl.formatMessage(messages.selectFile)}
            </Button>
            <view.div css={{marginLeft: 'small', font: 'label.small'}}>
              {intl.formatMessage(messages.orDragAndDropHere)}
            </view.div>
          </view.div>
        )}
        {suffix && (
          <view.span
            css={{
              font: 'label.small.emphasized',
              color: 'subdued',
            }}
          >
            {suffix}
          </view.span>
        )}
      </Button>
      {suffixLabel && (
        <view.span
          css={{
            font: 'label.small',
            color: 'subdued',
          }}
        >
          {suffixLabel}
        </view.span>
      )}
    </view.div>
  );

  return enableDropZone ? (
    <Dropzone
      accept={acceptableFileTypes ? [...acceptableFileTypes] : undefined}
      multiple={!singleFile}
      onDrop={handleFileDrop}
    >
      {({getRootProps}) => (
        <view.div {...getRootProps()}>{uploadInput}</view.div>
      )}
    </Dropzone>
  ) : (
    uploadInput
  );
};
